import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useBoolean } from 'usehooks-ts';

import type { V1CredentialInfo } from '@/generated/hooks/grimoire/models';
import type {
  Bdkv2BookDescriptor,
  V2BookConnectionDescriptor,
} from '@/generated/hooks/bdk/models';
import {
  getListAutomationsQueryKey,
  getListAutomationsInfiniteQueryKey,
  getListAutomationSummariesQueryKey,
  getListAutomationSummariesInfiniteQueryKey,
  getListAutomationRevisionSummariesQueryKey,
  usePublishAutomation,
  useValidateAutomationPublish,
  getQueryAutomationQueryKey,
} from '@/generated/hooks/grimoire/v1/api';
import { useRetrieveAllWorkspaceBookConnections } from '@/generated/hooks/bdk/v2/services/library';
import {
  getGetCollectionQueryKey,
  getListCollectionsQueryKey,
  publishCollection,
} from '@/generated/hooks/bishop/collections/v1/api';
import { useCurrentUser } from '@/generated/hooks/agora/v1/user';
import { captureException } from '@/modules/sentry';
import {
  AddConnectionDialog,
  EditConnectionDialog,
  SelectConnectionDialog,
  useConnectionActions,
  type IFilteredConnection,
} from '@/modules/integrations';
import { getRoutePath } from '@/routes/util';
import { showToast } from '@/shared/utils';
import { errorHandler } from '@/shared/utils/error-handler';

import DraftPublishDialog from './DraftPublishDialog';
import { useAutomationDraftCollections } from '../hooks/useAutomationDraftCollections';
import type {
  IDraftPublishManagerProps,
  ISelectedConnectionInfo,
  IConnectionCallbackParams,
  IOptionalConnectionCallbackParams,
  IPublishCollectionItem,
} from '../types';

/**
 * DraftPublishManager component for handling automation publishing.
 *
 * Manages the publish flow including:
 * - Validation to check for missing credentials
 * - Connection selection for required credentials
 * - Publishing with resolved credentials
 *
 * Shows a loading state while validating, then displays either:
 * - Simple dialog with version info (no credentials needed)
 * - Dialog with connection selection dropdowns (credentials needed)
 */
function DraftPublishManager({
  draftId,
  displayName,
  currentOrganizationId,
  currentWorkspaceId,
  draftVersion,
  isOpen,
  onClose,
}: IDraftPublishManagerProps) {
  const navigate = useNavigate();
  const { t: tDrafts } = useTranslation('drafts');
  const { t: tCommon } = useTranslation('common');
  const queryClient = useQueryClient();

  // State to track selected connections for credentials that need to be provided
  const [selectedConnectionIds, setSelectedConnectionIds] = useState<{
    missing_test_and_prod_creds?: Array<ISelectedConnectionInfo | null>;
    missing_prod_creds?: Array<Omit<
      ISelectedConnectionInfo,
      'isSeparateConnections'
    > | null>;
  }>({
    missing_test_and_prod_creds: [],
    missing_prod_creds: [],
  });

  // State for connection dialog
  const [selectedApp, setSelectedApp] = useState<{
    credentialInfo?: V1CredentialInfo;
    idx?: number;
    isProductionOnly?: boolean;
    integration?: Bdkv2BookDescriptor;
  } | null>(null);
  const {
    value: isConnectionDialogOpen,
    setTrue: openConnectionDialog,
    setFalse: closeConnectionDialog,
  } = useBoolean();
  const [connectionDialogType, setConnectionDialogType] = useState<
    'add' | 'edit' | 'select' | null
  >(null);

  // Validation mutation
  const {
    mutate: validatePublish,
    isPending: isValidating,
    isError: isValidationError,
    data: validationResponse,
  } = useValidateAutomationPublish();

  // Publish mutation
  const { mutate: publishAutomation, isPending: isPublishing } =
    usePublishAutomation();

  // Collections referenced by the automation with schema changes to promote.
  const {
    collectionsToPublish,
    isLoading: isLoadingCollections,
    isError: isCollectionsError,
  } = useAutomationDraftCollections(
    currentOrganizationId,
    currentWorkspaceId,
    draftId,
    isOpen,
  );
  const {
    value: isPublishingCollections,
    setTrue: startPublishingCollections,
    setFalse: stopPublishingCollections,
  } = useBoolean(false);

  // Actor recorded on collection publishes (same resource name used for created_by).
  const { data: currentUserData } = useCurrentUser();
  const publishedBy = currentUserData?.data?.user?.name;

  // Fetch workspace connections and actions
  const { data: connectionsData } = useRetrieveAllWorkspaceBookConnections(
    currentOrganizationId,
    currentWorkspaceId,
  );

  const { createConnection, updateConnection } = useConnectionActions(
    currentOrganizationId,
    currentWorkspaceId,
  );

  // Trigger validation when dialog opens
  // eslint-disable-next-line no-restricted-syntax -- reason: Validation gives us required credentials
  useEffect(() => {
    if (isOpen) {
      validatePublish(
        {
          organization: currentOrganizationId,
          workspace: currentWorkspaceId,
          automation: draftId,
          data: {},
        },
        {
          // Initialize selected connection ids placeholder based on validation response
          onSuccess: response => {
            if (response?.data) {
              const testAndProdCount =
                response.data.missing_test_and_prod_creds?.length ?? 0;
              const prodOnlyCount =
                response.data.missing_prod_creds?.length ?? 0;

              setSelectedConnectionIds({
                missing_test_and_prod_creds: Array(testAndProdCount).fill(null),
                missing_prod_creds: Array(prodOnlyCount).fill(null),
              });
            }
          },
          onError: error => {
            errorHandler(error, () => {
              captureException(
                error instanceof Error ? error : new Error(String(error)),
              );
            });
          },
        },
      );
    }
  }, [
    isOpen,
    validatePublish,
    currentOrganizationId,
    currentWorkspaceId,
    draftId,
    tCommon,
  ]);

  /**
   * Unified handler for selecting/updating a connection.
   * Handles connection creation, editing, and selection from dropdown.
   *
   * @param connectionData - Connection data with draft and published connection IDs and names
   * @param options - Configuration for toast message, cleanup actions, etc.
   */
  const handleConnectionSelection = (
    connectionData: {
      draftConnectionId: string;
      draftConnectionName?: string;
      publishedConnectionId: string;
      publishedConnectionName?: string;
    },
    options?: {
      toastMessage?: string;
    },
  ) => {
    const { credentialInfo, idx, isProductionOnly } = selectedApp || {};

    if (!credentialInfo || idx === undefined) return;

    // Use draft connection info for the main connection display
    const connectionId = connectionData.draftConnectionId;

    // Create selected connection info
    const selectedConnectionInfo: ISelectedConnectionInfo = {
      credentialInfo: {
        book: credentialInfo.book,
        book_version: credentialInfo.book_version,
        connection_id: connectionId,
        connection_alias: credentialInfo.connection_alias,
      },
      draftConnectionId: connectionData.draftConnectionId,
      publishedConnectionId: connectionData.publishedConnectionId,
    };

    // Update selected connections
    setSelectedConnectionIds(prev => {
      const newState = { ...prev };
      const key = isProductionOnly
        ? 'missing_prod_creds'
        : 'missing_test_and_prod_creds';

      newState[key] = [...(prev[key] || [])];
      newState[key][idx] = selectedConnectionInfo;

      return newState;
    });

    // Show toast if provided
    if (options?.toastMessage) {
      showToast(options.toastMessage, undefined, 'success');
    }

    // Cleanup actions
    closeConnectionDialog();
    setSelectedApp(null);
    setConnectionDialogType(null);
  };

  /**
   * Handles successful connection creation.
   * Auto-selects the newly created connection.
   */
  const handleConnectionDialogSuccess = (
    draftConnection?: V2BookConnectionDescriptor,
    publishedConnection?: V2BookConnectionDescriptor,
  ) => {
    if (
      !draftConnection?.connection_id ||
      !publishedConnection?.connection_id
    ) {
      showToast(
        tDrafts('drafts.publishDialog.connectionIncomplete'),
        undefined,
        'warning',
      );
      closeConnectionDialog();
      setSelectedApp(null);
      return;
    }

    handleConnectionSelection(
      {
        draftConnectionId: draftConnection.connection_id,
        draftConnectionName: draftConnection.connection_name,
        publishedConnectionId: publishedConnection.connection_id,
        publishedConnectionName: publishedConnection.connection_name,
      },
      {
        toastMessage: tDrafts(
          'drafts.publishDialog.connectionCreatedAndSelected',
        ),
      },
    );
  };

  /**
   * Handles successful connection edit (for missing_prod_creds case).
   * Verifies both draft and published connections exist, then auto-selects.
   */
  const handleEditConnectionDialogSuccess = async (
    draftConnection?: V2BookConnectionDescriptor,
    publishedConnection?: V2BookConnectionDescriptor,
  ) => {
    if (
      !draftConnection?.connection_id ||
      !publishedConnection?.connection_id
    ) {
      showToast(
        tDrafts('drafts.publishDialog.connectionIncomplete'),
        undefined,
        'warning',
      );
      closeConnectionDialog();
      setConnectionDialogType(null);
      setSelectedApp(null);
      return;
    }

    handleConnectionSelection(
      {
        draftConnectionId: draftConnection.connection_id,
        draftConnectionName: draftConnection.connection_name,
        publishedConnectionId: publishedConnection.connection_id,
        publishedConnectionName: publishedConnection.connection_name,
      },
      {
        toastMessage: tDrafts(
          'drafts.publishDialog.productionCredentialsAddedAndSelected',
        ),
      },
    );
  };

  /**
   * Handles opening the connection dialog (Add or Edit based on context).
   * - If connection name exists and it's production-only: Use EditConnectionDialog
   * - Otherwise: Use AddConnectionDialog
   */
  const handleSelectConnectionClick = ({
    credentialInfo,
    idx,
    integration,
  }: IConnectionCallbackParams) => {
    setSelectedApp({
      credentialInfo,
      idx,
      integration,
    });

    setConnectionDialogType('select');
    openConnectionDialog();
  };

  /**
   * Handles adding a new connection from the SelectConnectionDialog.
   * Called when the user clicks the "Add New Connection" button in SelectConnectionDialog.
   */
  const handleAddNewConnection = (
    params?: IOptionalConnectionCallbackParams,
  ) => {
    if (
      params?.idx !== undefined &&
      params?.credentialInfo &&
      params?.integration
    ) {
      setSelectedApp({
        credentialInfo: params.credentialInfo,
        idx: params.idx,
        integration: params.integration,
      });
    }
    setConnectionDialogType('add');
    openConnectionDialog();
  };

  /**
   * Handles adding production credentials for an integration that has a draft connection
   * already used in the automation but doesn't have a published connection.
   */
  const handleAddProdConnection = (
    { credentialInfo, idx, integration }: IConnectionCallbackParams,
    isProductionOnly: boolean = true,
  ) => {
    setSelectedApp({
      credentialInfo,
      idx,
      isProductionOnly,
      integration,
    });

    setConnectionDialogType('edit');
    openConnectionDialog();
  };

  /**
   * Handles existing connection selection from SelectConnectionDialog.
   * Routes to handleConnectionSelection if production credentials exist,
   * otherwise handleAddProdConnection.
   */
  const handleSelectExistingConnection = (connection: IFilteredConnection) => {
    if (
      !selectedApp?.credentialInfo ||
      !selectedApp?.integration ||
      selectedApp.idx === undefined
    ) {
      return;
    }

    const draftConnection = connection.draft_connection;
    const publishedConnection = connection.published_connection;

    if (connection.has_published_connection) {
      if (
        !draftConnection?.connection_id ||
        !publishedConnection?.connection_id
      ) {
        showToast(
          tDrafts('drafts.publishDialog.connectionIncomplete'),
          undefined,
          'warning',
        );
        closeConnectionDialog();
        setConnectionDialogType(null);
        setSelectedApp(null);
        return;
      }
      handleConnectionSelection({
        draftConnectionId: draftConnection.connection_id,
        draftConnectionName: draftConnection.connection_name,
        publishedConnectionId: publishedConnection.connection_id,
        publishedConnectionName: publishedConnection.connection_name,
      });
      setConnectionDialogType(null);
      setSelectedApp(null);
    } else {
      handleAddProdConnection(
        {
          credentialInfo: {
            ...selectedApp.credentialInfo,
            connection_id: connection.connection_id,
          },
          idx: selectedApp.idx,
          integration: selectedApp.integration,
        },
        // Although we will be adding only production credentials,
        // We need to set details in both test and prod connections in the requirements
        // As the test credentials are yet used in the automation.
        false,
      );
      setConnectionDialogType('edit');
    }
  };

  /**
   * Handles publish action.
   * Collects all selected connections and passes them to the publish API.
   *
   * Note: Only send missing_test_and_prod_creds (placeholder connections).
   * missing_prod_creds are real connections that just need production credentials
   * configured on the connection itself, not sent during publish.
   */
  // Invalidate both the list and per-collection caches so a retry after a
  // partial failure re-reads each stage's schema version and only publishes
  // what is still behind.
  const invalidateCollectionQueries = (
    collections: IPublishCollectionItem[],
  ) => {
    queryClient.invalidateQueries({
      queryKey: getListCollectionsQueryKey(
        currentOrganizationId,
        currentWorkspaceId,
      ),
    });
    collections.forEach(collection => {
      queryClient.invalidateQueries({
        queryKey: getGetCollectionQueryKey(
          currentOrganizationId,
          currentWorkspaceId,
          collection.id,
        ),
      });
    });
  };

  const handlePublish = async () => {
    // Publish the referenced collections first. If any fails, block the
    // automation publish since the published automation depends on them.
    if (collectionsToPublish.length > 0) {
      if (!publishedBy) {
        showToast(
          tDrafts('drafts.publishDialog.error.collectionsFailed.title'),
          tDrafts('drafts.publishDialog.error.collectionsFailed.description'),
          'error',
        );
        return;
      }
      startPublishingCollections();
      try {
        await Promise.all(
          collectionsToPublish.map(collection =>
            publishCollection(
              currentOrganizationId,
              currentWorkspaceId,
              collection.id,
              { published_by: publishedBy },
            ),
          ),
        );
        invalidateCollectionQueries(collectionsToPublish);
      } catch (error) {
        // Refetch so the hook drops the ones that did land and a retry only
        // publishes the rest.
        invalidateCollectionQueries(collectionsToPublish);
        errorHandler(error, () => {
          showToast(
            tDrafts('drafts.publishDialog.error.collectionsFailed.title'),
            tDrafts('drafts.publishDialog.error.collectionsFailed.description'),
            'error',
          );
          captureException(
            error instanceof Error ? error : new Error(String(error)),
          );
        });
        return;
      } finally {
        stopPublishingCollections();
      }
    }

    // Only collect missing_test_and_prod_creds (placeholder connections that need resolution)
    // Do NOT include missing_prod_creds - those are real connections already configured
    const credentials = [
      ...(selectedConnectionIds.missing_test_and_prod_creds
        ?.filter((c): c is ISelectedConnectionInfo => c !== null)
        .map(c => c.credentialInfo) ?? []),
    ];

    publishAutomation(
      {
        organization: currentOrganizationId,
        workspace: currentWorkspaceId,
        automation: draftId,
        data: {
          resolved_placeholder_test_credentials: credentials,
        },
      },
      {
        onSuccess: () => {
          showToast(
            tDrafts('drafts.publishDialog.success.title', {
              displayName,
            }),
            tDrafts('drafts.publishDialog.success.description'),
            'success',
          );
          queryClient.invalidateQueries({
            queryKey: getListAutomationsQueryKey(
              currentOrganizationId,
              currentWorkspaceId,
            ),
          });
          queryClient.invalidateQueries({
            queryKey: getListAutomationSummariesQueryKey(
              currentOrganizationId,
              currentWorkspaceId,
            ),
          });
          queryClient.invalidateQueries({
            queryKey: getListAutomationsInfiniteQueryKey(
              currentOrganizationId,
              currentWorkspaceId,
            ),
          });
          queryClient.invalidateQueries({
            queryKey: getListAutomationSummariesInfiniteQueryKey(
              currentOrganizationId,
              currentWorkspaceId,
            ),
          });
          // Invalidate revision summaries so version history and the
          // export/move version pickers all show the new published version —
          // they read the same summaries endpoint.
          queryClient.invalidateQueries({
            queryKey: getListAutomationRevisionSummariesQueryKey(
              currentOrganizationId,
              currentWorkspaceId,
              draftId,
            ),
          });
          // Remove automation data from cache since draft no longer exists
          queryClient.removeQueries({
            queryKey: getQueryAutomationQueryKey(
              currentOrganizationId,
              currentWorkspaceId,
              draftId,
            ),
          });
          onClose();
          navigate(
            getRoutePath('AUTOMATION_DETAILS', {
              orgId: currentOrganizationId,
              workspaceId: currentWorkspaceId,
              automationId: draftId,
            }),
          );
        },
        onError: error => {
          errorHandler(error, () => {
            showToast(
              tDrafts('drafts.publishDialog.error.publishFailed.title', {
                automationDisplayName: displayName,
              }),
              tDrafts('drafts.publishDialog.error.publishFailed.description'),
              'error',
            );
            captureException(
              error instanceof Error ? error : new Error(String(error)),
            );
          });
        },
      },
    );
  };

  /**
   * Handles connection dialog cancellation.
   * Closes the entire publish flow.
   */
  const handleConnectionDialogCancel = () => {
    closeConnectionDialog();
    setConnectionDialogType(null);
    setSelectedApp(null);
    onClose(); // Close the entire publish flow
  };

  const connectionName =
    connectionsData?.data?.book_connections?.find(
      connection =>
        connection.connection_id === selectedApp?.credentialInfo?.connection_id,
    )?.connection_name || '';

  return (
    <>
      <DraftPublishDialog
        open={isOpen}
        onClose={onClose}
        displayName={displayName}
        isValidating={isValidating}
        isValidationError={isValidationError}
        requiresAuthentication={
          validationResponse?.data?.missing_test_and_prod_creds || []
        }
        requiresProductionCredentials={
          validationResponse?.data?.missing_prod_creds || []
        }
        draftVersion={draftVersion}
        collectionsToPublish={collectionsToPublish}
        isLoadingCollections={isLoadingCollections}
        isCollectionsError={isCollectionsError}
        publishedBy={publishedBy}
        onSelectConnectionClick={handleSelectConnectionClick}
        onAddNewConnection={handleAddNewConnection}
        onAddProdConnection={handleAddProdConnection}
        onPublish={handlePublish}
        selectedConnectionIds={selectedConnectionIds}
        isPublishing={isPublishing || isPublishingCollections}
      />
      {selectedApp?.integration && isConnectionDialogOpen && (
        <>
          {connectionDialogType === 'add' && (
            <AddConnectionDialog
              showSuccessToast={false}
              isOpen={isConnectionDialogOpen}
              integration={selectedApp.integration}
              requireProductionCredentials
              createConnection={createConnection}
              updateConnection={updateConnection}
              onSuccess={handleConnectionDialogSuccess}
              onCancel={handleConnectionDialogCancel}
            />
          )}
          {connectionDialogType === 'edit' && (
            <EditConnectionDialog
              showSuccessToast={false}
              isOpen={isConnectionDialogOpen}
              integration={selectedApp.integration}
              connections={
                connectionsData?.data?.book_connections?.filter(
                  conn =>
                    conn.book_name === selectedApp.credentialInfo?.book &&
                    conn.book_version ===
                      selectedApp.credentialInfo?.book_version &&
                    conn.connection_name === connectionName,
                ) || []
              }
              existingConnectionNames={
                connectionsData?.data?.book_connections?.flatMap(conn =>
                  conn.connection_name ? [conn.connection_name] : [],
                ) || []
              }
              publishOnlyMode
              createConnection={createConnection}
              updateConnection={updateConnection}
              onSuccess={handleEditConnectionDialogSuccess}
              onCancel={handleConnectionDialogCancel}
            />
          )}
          {connectionDialogType === 'select' && (
            <SelectConnectionDialog
              isOpen={isConnectionDialogOpen}
              integration={selectedApp.integration}
              connections={connectionsData?.data?.book_connections || []}
              onClose={handleConnectionDialogCancel}
              onAddConnection={handleAddNewConnection}
              onSelectConnection={handleSelectExistingConnection}
            />
          )}
        </>
      )}
    </>
  );
}

export default DraftPublishManager;
